import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as SalesIcon,
  People as PeopleIcon,
  Star as StarIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { pl } from 'date-fns/locale';

const EmployeeStats = ({ sales, clients, employees, selectedMonth, employeeId }) => {
  const [employeeData, setEmployeeData] = useState(null);
  const [salesStats, setSalesStats] = useState({
    currentMonth: 0,
    previousMonth: 0,
    trend: 0,
    count: 0
  });
  const [clientStats, setClientStats] = useState({
    total: 0,
    active: 0,
    new: 0
  });
  const [recentSales, setRecentSales] = useState([]);
  const [achievements, setAchievements] = useState([]);
  
  useEffect(() => {
    if (!sales || !clients || !employees || !employeeId) return;

    const employee = employees.find(emp => emp.id === employeeId);
    setEmployeeData(employee || null);

    calculateSalesStats(sales, employeeId, selectedMonth);
    calculateClientStats(clients, employeeId, selectedMonth);
    getRecentSales(sales, employeeId);
    generateAchievements(sales, employeeId, selectedMonth);

  }, [sales, clients, employees, selectedMonth, employeeId]);
  
  const calculateSalesStats = (sales, employeeId, selectedMonth) => {
    if (!sales || sales.length === 0) return;
    
    const currentMonthStart = startOfMonth(selectedMonth);
    const currentMonthEnd = endOfMonth(selectedMonth);
    const previousMonthStart = startOfMonth(subMonths(selectedMonth, 1));
    const previousMonthEnd = endOfMonth(subMonths(selectedMonth, 1));
    
    const currentMonthSales = sales.filter(sale => {
      const saleDate = sale.completedAt ? new Date(sale.completedAt) : null;
      return sale.employee_id === employeeId && saleDate &&
             saleDate >= currentMonthStart && saleDate <= currentMonthEnd;
    });
    
    const previousMonthSales = sales.filter(sale => {
      const saleDate = sale.completedAt ? new Date(sale.completedAt) : null;
      return sale.employee_id === employeeId && saleDate &&
             saleDate >= previousMonthStart && saleDate <= previousMonthEnd;
    });
    
    const currentMonthTotal = currentMonthSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
    const previousMonthTotal = previousMonthSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
    
    let trend = 0;
    if (previousMonthTotal > 0) {
      trend = ((currentMonthTotal - previousMonthTotal) / previousMonthTotal) * 100;
    } else if (currentMonthTotal > 0) {
      trend = 100;
    }
    
    setSalesStats({
      currentMonth: currentMonthTotal,
      previousMonth: previousMonthTotal,
      trend: trend,
      count: currentMonthSales.length
    });
  };
  
  const calculateClientStats = (clients, employeeId, selectedMonth) => {
    if (!clients || clients.length === 0) return;
    
    const assignedClients = clients.filter(client => client.assigned_to === employeeId);
    const activeClients = assignedClients.filter(client => client.status === 'active');
    
    const newClients = assignedClients.filter(client => {
      const creationDate = client.createdAt ? new Date(client.createdAt) : null;
      return creationDate && creationDate >= startOfMonth(selectedMonth) && creationDate <= endOfMonth(selectedMonth);
    });
    
    setClientStats({
      total: assignedClients.length,
      active: activeClients.length,
      new: newClients.length
    });
  };
  
  const getRecentSales = (sales, employeeId) => {
    if (!sales || sales.length === 0) return;
    
    const employeeSales = sales
      .filter(sale => sale.employee_id === employeeId)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      .slice(0, 5);
      
    setRecentSales(employeeSales);
  };
  
  const generateAchievements = (sales, employeeId, selectedMonth) => {
    if (!sales || sales.length === 0) return;

    const currentMonthStart = startOfMonth(selectedMonth);
    const currentMonthEnd = endOfMonth(selectedMonth);

    const employeeSales = sales.filter(sale => 
        sale.employee_id === employeeId &&
        new Date(sale.completedAt) >= currentMonthStart &&
        new Date(sale.completedAt) <= currentMonthEnd
    );

    const salesCount = employeeSales.length;
    const totalTurnover = employeeSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);

    const newAchievements = [];

    // Osiągnięcie: Pierwsza sprzedaż w miesiącu
    if (salesCount > 0) {
        newAchievements.push({
            icon: <StarIcon />,
            title: 'Dobry początek!',
            description: 'Zanotowano pierwszą sprzedaż w tym miesiącu.',
            color: 'info'
        });
    }

    // Osiągnięcie: 5 sprzedaży w miesiącu
    if (salesCount >= 5) {
        newAchievements.push({
            icon: <SalesIcon />,
            title: 'Sprzedażowy maraton',
            description: 'Osiągnięto 5 sprzedaży w tym miesiącu. Brawo!',
            color: 'primary'
        });
    }

    // Osiągnięcie: Obrót powyżej 500 000 PLN
    if (totalTurnover >= 500000) {
        newAchievements.push({
            icon: <MoneyIcon />,
            title: 'Złoty sprzedawca',
            description: 'Obrót w tym miesiącu przekroczył 500 000 PLN.',
            color: 'success'
        });
    }

    // Osiągnięcie: Obrót powyżej 1 000 000 PLN
    if (totalTurnover >= 1000000) {
        newAchievements.push({
            icon: <TrophyIcon />,
            title: 'Mistrz obrotu',
            description: 'Niesamowite! Obrót przekroczył 1 000 000 PLN.',
            color: 'warning'
        });
    }

    setAchievements(newAchievements);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', { 
      style: 'currency', 
      currency: 'PLN' 
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), 'd MMMM yyyy', { locale: pl });
  };

  if (!employeeData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Ładowanie danych pracownika...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Witaj, {employeeData.firstName}!
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Oto Twoje podsumowanie na {format(selectedMonth, 'MMMM yyyy', { locale: pl })}.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Statystyki Główne */}
        <Grid item xs={12} md={6} lg={4}>
          <Card elevation={3}>
            <CardHeader 
              title="Obrót w tym miesiącu"
              avatar={<MoneyIcon color="success" />}
            />
            <CardContent>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formatCurrency(salesStats.currentMonth)}
              </Typography>
              <Typography 
                variant="body2" 
                component="div"
                color={salesStats.trend >= 0 ? 'success.main' : 'error.main'}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {salesStats.trend >= 0 ? <TrendingUpIcon sx={{ mr: 0.5 }} /> : <TrendingDownIcon sx={{ mr: 0.5 }} />}
                {salesStats.trend.toFixed(1)}% vs poprzedni miesiąc
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card elevation={3}>
            <CardHeader title="Liczba sprzedaży" avatar={<SalesIcon color="info" />} />
            <CardContent>
              <Typography variant="h4" component="div" fontWeight="bold">
                {salesStats.count}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                Transakcji w tym miesiącu
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card elevation={3}>
            <CardHeader title="Twoi klienci" avatar={<PeopleIcon color="warning" />} />
            <CardContent>
              <Typography variant="h4" component="div" fontWeight="bold">
                {clientStats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                Przypisanych klientów ({clientStats.new} nowych)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Cel miesięczny - przykład */}
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader title="Cel miesięczny" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom component="div">
                Postęp w realizacji celu obrotu (200 000 PLN)
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(salesStats.currentMonth / 200000) * 100}
                sx={{ height: 10, borderRadius: 5, mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader title="Twoje ostatnie sprzedaże" />
            <CardContent sx={{ p: 0 }}>
              {recentSales.length > 0 ? (
                <List>
                  {recentSales.map((sale) => (
                    <ListItem key={sale.id} divider>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <MoneyIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              {sale.productDetails?.brand || ''} {sale.productDetails?.model || 'Produkt'}
                            </Typography>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {formatCurrency(sale.amount || 0)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(sale.completedAt)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {sale.clientName || 'Klient'}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  Brak ostatnich sprzedaży
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader 
              title="Twoje osiągnięcia" 
              avatar={<TrophyIcon color="warning" />}
            />
            <CardContent sx={{ p: 0 }}>
              {achievements.length > 0 ? (
                <List>
                  {achievements.map((achievement, index) => (
                    <ListItem key={index} divider={index < achievements.length - 1}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: `${achievement.color}.main` }}>
                          {achievement.icon}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ mr: 1 }}>
                              {achievement.title}
                            </Typography>
                            <Chip 
                              size="small" 
                              color={achievement.color} 
                              label="Nowe" 
                              sx={{ height: 20 }}
                            />
                          </Box>
                        }
                        secondary={achievement.description}
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  Brak osiągnięć w tym miesiącu. Pracuj dalej, aby zdobyć nowe odznaki!
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmployeeStats;
